import { atom } from 'jotai';

import { officeRoutes } from '../routes';
import { DocumentPermission } from '../types';

import { atomFromQueryString, atomFromRoutePath } from '~/shared/state';

// 🟣🟣🟣 PARAMS
const $editorParam = atomFromRoutePath(officeRoutes.editor);
export const $fileId = atom((get) => get($editorParam)?.fileId);
export const $permission =
  atomFromQueryString.string<DocumentPermission>('permission');

// 🔵🔵🔵 STATE -> Current editing session
export interface EditingSession {
  fileId: string;
  editUrl: string;
  collaboraUrl: string;
  token: string;
  isLoading: boolean;
  error?: string;
}

export const $currentEditingSession = atom<EditingSession | null>(null);

// 🔵🔵🔵 STATE -> PostMessage event handler
export interface PostMessageEvent {
  type: string;
  data: unknown;
  timestamp: number;
}

export const $lastPostMessageEvent = atom<PostMessageEvent | null>(null);

// 🔵🔵🔵 STATE -> Editor ready state
export const $isEditorReady = atom(false);

// 🔵🔵🔵 STATE -> Document info
export interface DocumentInfo {
  documentName?: string;
  documentType?: string;
  canEdit: boolean;
  canExport: boolean;
  canPrint: boolean;
}

export const $documentInfo = atom<DocumentInfo>({
  canEdit: true,
  canExport: true,
  canPrint: true,
});

// 🟡🟡🟡 DERIVED STATE -> Is document loaded
export const $isDocumentLoaded = atom((get) => {
  const session = get($currentEditingSession);
  const isReady = get($isEditorReady);
  return session !== null && isReady && !session.isLoading;
});

// 🟠🟠🟠 ACTION -> Send postMessage to Collabora iframe
export const $sendCollaboraMessage = atom(
  null,
  (get, set, { type, data }: { type: string; data: unknown }) => {
    const iframe = document.getElementById(
      'collabora-iframe',
    ) as HTMLIFrameElement;
    if (!iframe?.contentWindow) {
      console.error('Collabora iframe not found');
      return;
    }

    const message = {
      MessageId: type,
      Values: data,
    };

    const session = get($currentEditingSession);
    if (session?.collaboraUrl) {
      iframe.contentWindow.postMessage(
        JSON.stringify(message),
        session.collaboraUrl,
      );
    }
  },
);

// 🟠🟠🟠 ACTION -> Handle postMessage from Collabora
export const $handleCollaboraMessage = atom(
  null,
  (get, set, event: MessageEvent) => {
    const session = get($currentEditingSession);
    if (!session?.collaboraUrl) return;

    // Verify origin
    const collaboraOrigin = new URL(session.collaboraUrl).origin;
    if (event.origin !== collaboraOrigin) {
      console.warn('Ignoring message from unexpected origin:', event.origin);
      return;
    }

    try {
      const message =
        typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

      // Store the last message
      set($lastPostMessageEvent, {
        type: message.MessageId || message.type || 'unknown',
        data: message.Values || message,
        timestamp: Date.now(),
      });

      // Handle specific message types
      switch (message.MessageId || message.type) {
        case 'App_LoadingStatus':
          if (message.Values?.Status === 'Document_Loaded') {
            set($isEditorReady, true);
          }
          break;

        case 'Doc_ModifiedStatus':
          // Document has been modified
          console.log('Document modified:', message.Values?.Modified);
          break;

        case 'Action_Save_Resp':
          // Save completed
          console.log('Document saved:', message);
          break;

        case 'UI_FileVersions':
          // Version history available
          console.log('File versions:', message.Values);
          break;

        case 'Get_Views_Resp':
          // Views/users in the document
          console.log('Document views:', message.Values);
          break;

        default:
          console.log('Unhandled Collabora message:', message);
      }
    } catch (error) {
      console.error('Error handling Collabora message:', error);
    }
  },
);
